// Set up LAMP (onboarding, VS0): one question at a time, each answer kept as it is given so the Owner can leave
// and come back, then the activation summary confirmed once. A business LAMP does not serve yet joins the waitlist.
// The questions and their choices are in the person's language from the first one (#465): the catalogue words them by
// id, and the priorities and Focus Areas come from the api in the person's language, read again when it changes.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  ApiError,
  type ActivationSummary,
  type Api,
  type Onboarding as OnboardingData,
  type OnboardingAnswer,
  type OnboardingQuestion,
} from "../api.js";
import { locale } from "../i18n/format.js";
import type { Language } from "../i18n/language.js";
import { useLanguage, useWords, type Words } from "../i18n/words.js";

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError
    ? error.problems.length > 0
      ? error.problems.join("; ")
      : w.ownerPagesRefused(error.code)
    : w.ownerPagesUnreachable;

/** The last day of a trial whose first day after it is `endsOn`, e.g. "26 October 2026". */
export function lastTrialDay(endsOn: string, language: Language = "en"): string {
  const day = new Date(`${endsOn}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() - 1);
  return new Intl.DateTimeFormat(locale(language), {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(day);
}

/** The ranks a ranked question asks for, as catalogue keys. */
const RANKS = ["onboardingMostImportant", "onboardingThen", "onboardingOptional"] as const;

function QuestionForm({
  question,
  gstin,
  busy,
  onAnswer,
}: {
  question: OnboardingQuestion;
  /** The GSTIN the lookup could not find, which the registration details question names. */
  gstin: string | undefined;
  busy: boolean;
  onAnswer: (answer: OnboardingAnswer) => void;
}) {
  const w = useWords();
  const [one, setOne] = useState("");
  const [many, setMany] = useState<string[]>([]);
  const [ranked, setRanked] = useState<string[]>(["", "", ""]);
  const [text, setText] = useState("");
  const [particulars, setParticulars] = useState({ address: "", state: "", signatory: "" });
  const particular = (key: keyof typeof particulars) => (event: { target: { value: string } }) =>
    setParticulars({ ...particulars, [key]: event.target.value });

  const value = (): unknown => {
    switch (question.kind) {
      case "one":
        return one;
      case "many":
        return many;
      case "ranked":
        return ranked.filter((id) => id !== "");
      case "registration":
        return text.trim() === "" ? null : text.trim();
      case "registration_details":
        return { legalName: text.trim(), scheme: one };
      case "particulars":
        return {
          address: particulars.address.trim(),
          state: particulars.state.trim(),
          signatory: particulars.signatory.trim(),
        };
      case "names":
        return text
          .split("\n")
          .map((name) => name.trim())
          .filter((name) => name !== "");
    }
  };
  const ready =
    question.kind === "one"
      ? one !== ""
      : question.kind === "ranked"
        ? ranked.filter((id) => id !== "").length >= 2
        : question.kind === "registration_details"
          ? one !== "" && text.trim() !== ""
          : question.kind === "particulars"
            ? particulars.address.trim() !== "" &&
              /^\d{2}$/.test(particulars.state.trim()) &&
              particulars.signatory.trim() !== ""
            : true;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onAnswer({
      question: question.id,
      ...(question.dimension !== undefined && { dimension: question.dimension }),
      value: value(),
    });
  };

  const choices = (multiple: boolean) =>
    question.options.map((o) => (
      <label key={o.id} className="choice">
        <input
          type={multiple ? "checkbox" : "radio"}
          name={question.id}
          value={o.id}
          checked={multiple ? many.includes(o.id) : one === o.id}
          onChange={(e) =>
            multiple ? setMany(e.target.checked ? [...many, o.id] : many.filter((id) => id !== o.id)) : setOne(o.id)
          }
        />{" "}
        {w.onboardingOption(question.id, o)}
      </label>
    ));

  return (
    <form className="onboarding" onSubmit={submit} aria-label={w.onboardingQuestion}>
      <fieldset>
        <legend>{w.onboardingPrompt(question, gstin)}</legend>
        {question.kind === "one" ? choices(false) : null}
        {question.kind === "many" ? choices(true) : null}
        {question.kind === "ranked"
          ? RANKS.map((rank, i) => (
              <label key={rank} className="field">
                {w[rank]}{" "}
                <select
                  value={ranked[i]}
                  onChange={(e) => setRanked(ranked.map((id, j) => (j === i ? e.target.value : id)))}
                >
                  <option value="">{w.onboardingChoose}</option>
                  {question.options.map((o) => (
                    <option key={o.id} value={o.id}>
                      {w.onboardingOption(question.id, o)}
                    </option>
                  ))}
                </select>
              </label>
            ))
          : null}
        {question.kind === "registration" ? (
          <label className="field">
            {w.onboardingGstin}{" "}
            <input value={text} maxLength={15} autoComplete="off" onChange={(e) => setText(e.target.value)} />
          </label>
        ) : null}
        {question.kind === "registration_details" ? (
          <>
            <label className="field">
              {w.onboardingLegalName} <input value={text} onChange={(e) => setText(e.target.value)} />
            </label>
            {choices(false)}
          </>
        ) : null}
        {question.kind === "particulars" ? (
          <>
            <label className="field">
              {w.onboardingAddress}{" "}
              <textarea value={particulars.address} maxLength={500} rows={2} onChange={particular("address")} />
            </label>
            <label className="field">
              {w.onboardingState}{" "}
              <input
                value={particulars.state}
                maxLength={2}
                inputMode="numeric"
                autoComplete="off"
                onChange={particular("state")}
              />
            </label>
            <label className="field">
              {w.onboardingSignatory}{" "}
              <input value={particulars.signatory} maxLength={200} onChange={particular("signatory")} />
            </label>
          </>
        ) : null}
        {question.kind === "names" ? (
          <label className="field">
            {w.onboardingNames} <textarea value={text} rows={6} onChange={(e) => setText(e.target.value)} />
          </label>
        ) : null}
      </fieldset>
      <button type="submit" className="btn" disabled={busy || !ready}>
        {w.onboardingNext}
      </button>
    </form>
  );
}

function Summary({ summary, busy, onConfirm }: { summary: ActivationSummary; busy: boolean; onConfirm: () => void }) {
  const w = useWords();
  return (
    <section className="section" aria-labelledby="summary-heading">
      <h2 id="summary-heading">{w.onboardingReady}</h2>
      <p>{w.onboardingTemplate(w.onboardingVertical(summary.vertical))}</p>
      <ul className="plain-list">
        {summary.focusAreas.map((f) => (
          <li key={f.id}>{f.name}</li>
        ))}
      </ul>
      {summary.off && summary.off.length > 0 ? <p>{w.onboardingOff(summary.off.map((f) => f.name))}</p> : null}
      {summary.off?.some((f) => w.onboardingWhileOff(f.id, f.name) !== undefined) ? (
        <ul className="plain-list small">
          {summary.off.flatMap((f) => {
            const missed = w.onboardingWhileOff(f.id, f.name);
            return missed === undefined ? [] : [<li key={f.id}>{missed}</li>];
          })}
        </ul>
      ) : null}
      <p className="muted">
        {w.onboardingFromAnswers(
          Object.entries(summary.businessProfile)
            .flatMap(([key, value]) => w.onboardingProfile(key, value) ?? [])
            .join("; "),
          summary.trialDays,
        )}
      </p>
      {(summary.dimensionValues ?? []).map((given) => (
        <p key={given.dimension}>{w.onboardingAdds(given.label, given.names)}</p>
      ))}
      {summary.supplierParticulars ? (
        <p className="muted">
          {w.onboardingInvoicesCarry(
            summary.supplierParticulars.legalName,
            summary.supplierParticulars.address,
            summary.supplierParticulars.gstin,
            summary.supplierParticulars.signatory,
          )}
        </p>
      ) : null}
      <button type="button" className="btn" disabled={busy} onClick={onConfirm}>
        {w.onboardingSwitchOn}
      </button>
    </section>
  );
}

export function Onboarding({ api }: { api: Api }) {
  const w = useWords();
  const { language } = useLanguage();
  const [state, setState] = useState<OnboardingData | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  // Read again when the language changes: the priorities and Focus Areas come named in it.
  const load = useCallback(() => {
    api.onboarding().then(setState, (error: unknown) => setProblem(problemOf(error, w)));
  }, [api, language]);
  useEffect(load, [load]);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setProblem(undefined);
    try {
      await work();
    } catch (error) {
      setProblem(problemOf(error, w));
    } finally {
      setBusy(false);
    }
  };

  if (!state) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.loading}</p>;
  const alert = problem ? (
    <p className="alert" role="alert">
      {problem}
    </p>
  ) : null;

  if (state.status === "activated")
    return (
      <p role="status">
        {w.onboardingSetUp}
        {state.trial ? w.onboardingTrialUntil(lastTrialDay(state.trial.endsOn, language)) : ""}
      </p>
    );
  if (state.status === "waitlisted") return <p role="status">{w.onboardingWaitlisted}</p>;
  return (
    <>
      {alert}
      {state.question ? (
        <QuestionForm
          // Each Dimension set-up asks about is its own question: a fresh form, never the last one's names (#740).
          key={`${state.question.id}:${state.question.dimension ?? ""}`}
          question={state.question}
          gstin={typeof state.answers["pendingGstin"] === "string" ? state.answers["pendingGstin"] : undefined}
          busy={busy}
          onAnswer={(answer) => void run(async () => setState(await api.answerOnboarding(answer)))}
        />
      ) : state.summary ? (
        <Summary
          summary={state.summary}
          busy={busy}
          onConfirm={() =>
            void run(async () => {
              await api.activate();
              load();
            })
          }
        />
      ) : null}
    </>
  );
}
